import { useState, type ReactNode } from "react";

/* ------------------------------------------------------------------ */
/* PageHeader                                                          */
/* ------------------------------------------------------------------ */

export function PageHeader({
  icon,
  title,
  description,
  actions,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="header-title">
        {icon}
        <div>
          <h2>{title}</h2>
          {description ? <p className="page-desc">{description}</p> : null}
        </div>
      </div>
      {actions ? <div className="header-actions">{actions}</div> : null}
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* StatusChip                                                          */
/* ------------------------------------------------------------------ */

export type ChipTone = "ok" | "warn" | "danger" | "info" | "purple" | "neutral";

const CHIP_CLASS: Record<ChipTone, string> = {
  ok: "chip chip-green",
  warn: "chip chip-yellow",
  danger: "chip chip-red",
  info: "chip chip-blue",
  purple: "chip chip-purple",
  neutral: "chip",
};

export function StatusChip({
  tone = "neutral",
  children,
  title,
  icon,
}: {
  tone?: ChipTone;
  children: ReactNode;
  title?: string;
  icon?: ReactNode;
}) {
  return (
    <span className={CHIP_CLASS[tone]} title={title}>
      {icon}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* StatTile                                                            */
/* ------------------------------------------------------------------ */

export function StatTile({
  label,
  value,
  sub,
  icon,
  tone,
  hint,
  compactValue = false,
  valueTone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  tone?: "ok" | "warn" | "danger";
  hint?: string;
  compactValue?: boolean;
  valueTone?: "positive" | "negative" | "neutral";
}) {
  const toneClass =
    tone === "danger" ? "stat-danger" : tone === "warn" ? "stat-warn" : tone === "ok" ? "stat-ok" : "";
  return (
    <div className={`metric-tile stat-tile ${toneClass}`} title={hint}>
      <div className="metric-tile-head">
        <span className="metric-tile-label">{label}</span>
        {icon}
      </div>
      <div className={`metric-tile-value ${compactValue ? "stat-value-compact" : ""} ${valueTone ? `value-${valueTone}` : ""}`}>{value}</div>
      {sub ? <div className="metric-tile-sub">{sub}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* EmptyState / InfoTip / CopyButton                                   */
/* ------------------------------------------------------------------ */

export function EmptyState({
  icon,
  title,
  description,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
}) {
  return (
    <div className="ui-empty">
      {icon}
      <div className="ui-empty-title">{title}</div>
      {description ? <div className="ui-empty-desc">{description}</div> : null}
    </div>
  );
}

export function InfoTip({ text }: { text: string }) {
  return (
    <span className="ui-infotip" title={text} aria-label={text} role="img">
      ?
    </span>
  );
}

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="ui-copy"
      title={`${label ?? value} 복사`}
      onClick={(event) => {
        event.stopPropagation();
        void navigator.clipboard.writeText(value);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? "복사됨" : "복사"}
    </button>
  );
}
